// Shared SSE reader + text hygiene for the coach stream (GET /v2/stream, read
// by v2/client.js): the "data: {json}\n" protocol. One reader, one place to
// get the buffering right.

const HEALTH_DISCLAIMER_TAG = '[HEALTH_DISCLAIMER]';
// Strip EVERY occurrence — the coach sometimes emits the tag twice in a long
// answer (aithlo-mobile#39).
const HEALTH_DISCLAIMER_RE = /\[HEALTH_DISCLAIMER\]/g;
// Routing/delegation control tokens the model can leak into prose, including a
// paren-wrapped or space-after-colon form and a trailing bare word that streams
// a frame before its ":target".
const CONTROL_TOKEN_RE = /\(\s*(?:DELEGATE|ROUTE|OFFER):\s*\w*\s*\)|(?:DELEGATE|ROUTE|OFFER):\s*\w*/g;
const TRAILING_CONTROL_RE = /(?:DELEGATE|ROUTE|OFFER)\s*$/;
// A pasted Chart.js config (fenced or bare) — the chart renders itself from the
// final payload, so the JSON has no business in the prose.
const CHART_SPEC_RE = /```[a-z]*\s*(\{[\s\S]*?"datasets"[\s\S]*?\})\s*```|(?:^|\n)\s*\{"type":"[a-z]+","datasets":[\s\S]*?\}\s*(?=\n|$)/gi;

/** Clean streamed or stored coach prose. Returns { text, disclaimer }. */
export function cleanCoachText(raw, { streaming = false } = {}) {
  const source = raw || '';
  const disclaimer = source.includes(HEALTH_DISCLAIMER_TAG);
  let text = source
    .replace(CONTROL_TOKEN_RE, '')
    .replace(HEALTH_DISCLAIMER_RE, '')
    .replace(CHART_SPEC_RE, '');
  if (streaming) text = text.replace(TRAILING_CONTROL_RE, '').trimStart();
  else text = text.trim();
  return { text, disclaimer };
}

// How long the socket may go silent before the turn is treated as dead. The
// backend heartbeats every stream (with_heartbeat), so a healthy long planner
// run never gets near this; a proxy holding a dead socket open did, and the
// spinner sat there forever.
export const STALL_MS = 90 * 1000;

/**
 * Read a fetch Response body as SSE. Calls onEvent(data) per parsed event.
 * Stops on a `done` event or when the body ends. The reader is released on
 * every exit — including an onEvent that throws (an `error` event does).
 * Throws a `stalled` error when nothing at all arrives for `stallMs`, so the
 * caller reconciles against the server instead of waiting.
 */
export async function readSSE(response, onEvent, { stallMs = STALL_MS } = {}) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let stop = false;
  const readOrStall = () => new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error('The connection went quiet');
      err.stalled = true;
      reject(err);
    }, stallMs);
    reader.read().then((r) => { clearTimeout(timer); resolve(r); },
                       (e) => { clearTimeout(timer); reject(e); });
  });
  try {
    while (!stop) {
      const { done, value } = await readOrStall();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        let data;
        try {
          data = JSON.parse(line.slice(6));
        } catch {
          continue;
        }
        if (data.type === 'done') { stop = true; break; }
        onEvent(data);
      }
    }
  } finally {
    try { reader.cancel(); } catch { /* already closed */ }
  }
}

let _seq = 0;
export function newId(prefix = 'm') {
  _seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${_seq}`;
}
